/**
 * A basic button control.
 * ## Events
 * Name | Arguments | Description
 * click | A reference to `this` `ButtonView` and the original event. | Fired whenever the button is clicked.
 * @module app/views/ButtonView
 */
import Backbone from "backbone";
import _ from "underscore";
export default Backbone.View.extend({
  tagName: "button",
  attributes: {
    type: "button",
  },
  events: {
    click: function (evt) {
      if (this.options.toggleable) {
        this.toggle();
      }

      this.trigger("click", this, evt);
      this.options.click(this, evt);
    },
  },

  template: _.template(
    '<% if(icon){%><span class="glyphicon glyphicon-<%- icon%>"></span><% }%><%- text%>'
  ),

  /**
   * Initializes the control.
   * @param  {object} options The options bhash.
   * @param {string} options.type:'default' The theme for the control. One of `'default'`, `'primary'`, `'success'`, `'info'`, `'warning'`, `'danger'`, `'link'`.
   * @param {string} options.text:'Click' The text to display on the control.
   * @param {Function} options.click:noop The function to execute when the button is clicked. Given the same arguments as consumers of the `click` event.
   * @param {string} [options.className] Additional CSS classes to apply to the underlying HTML tag.
   * @param {string} [options.icon] An identifier for an icon to display in the button.
   * @param {boolean} [options.toggleable:false] True to indicate that "clicking" the button should toggle it between active and inactive, rather than the default, which does not retain state.
   * @param {boolean} [options.tooltip:''] Provide a non-empty string to affix a tooltip to the button.
   */
  initialize: function (options) {
    this.options = _.defaults({}, options, {
      type: "default",
      click: function () {},
      text: "Click",
      className: "",
      icon: "",
      toggleable: false,
      tooltip: "",
    });
    this._activated = false;
  },
  render: function () {
    this.$el.html(this.template(this.options));
    this.$el.addClass(
      "btn btn-" + this.options.type + " " + this.options.className
    );

    if (this.options.tooltip) {
      this.$el.tooltip({
        title: this.options.tooltip,
      });
    }

    this._rendered = true;

    return this;
  },

  /**
   * Toggles the current display state of the button between default and "activated".
   */
  toggle: function () {
    if (this._rendered) {
      this.$el.button("toggle");
      this._activated = !this._activated;

      this.trigger("toggle");

      if (this._activated) {
        this.trigger("activate");
      } else {
        this.trigger("deactivate");
      }
    }
  },

  /**
   * Set the display state of the button to "activated".
   */
  activate: function () {
    if (this._rendered) {
      if (!this._activated) {
        this.toggle();
      }
    }
  },

  /**
   * Set the display state of the button to default.
   */
  deactivate: function () {
    if (this._rendered) {
      if (this._activated) {
        this.toggle();
        this.$el.blur();
      }
    }
  },

  /**
   * Gets a value indicating whether or not the button is in the "activated" state.
   * @return {Boolean} True if in the "activated" state, otherwise false.
   */
  isActive: function () {
    return this._activated;
  },

  /**
   * Enable the button.
   */
  enable: function () {
    this.$el.removeAttr("disabled");
  },

  /**
   * Disable the button.
   */
  disable: function () {
    this.$el.attr("disabled", "disabled");
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/core/views/ButtonView.js
// module id = 37
// module chunks = 4